import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

interface FooterSettings {
  company_description: string;
  facebook_url: string;
  twitter_url: string;
  instagram_url: string;
  copyright_text: string;
  show_social_links: boolean;
  show_contact_info: boolean;
  show_company_info: boolean;
  show_footer_image: boolean;
}

const defaultFooterSettings: FooterSettings = {
  company_description: 'Bridging cultures through language learning with our unique approach combining Language, Culture, and Tourism.',
  facebook_url: '#',
  twitter_url: '#',
  instagram_url: '#',
  copyright_text: '© 2024 Nepal World Language Centre. All rights reserved.',
  show_social_links: true,
  show_contact_info: true,
  show_company_info: true,
  show_footer_image: true
};

export const useFooterSettings = () => {
  const [footerSettings, setFooterSettings] = useState<FooterSettings>(defaultFooterSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFooterSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('category', 'footer')
        .eq('is_active', true);

      if (fetchError) {
        throw fetchError;
      }

      if (data && data.length > 0) {
        const settings: Partial<FooterSettings> = {};
        
        data.forEach((setting: any) => {
          if (setting.setting_key in defaultFooterSettings) {
            const value = setting.setting_value;
            // Convert string booleans to actual booleans
            if (setting.setting_type === 'boolean') {
              (settings as any)[setting.setting_key] = value === 'true';
            } else {
              (settings as any)[setting.setting_key] = value;
            }
          }
        });

        setFooterSettings({ ...defaultFooterSettings, ...settings });
      } else {
        setFooterSettings(defaultFooterSettings);
      }
    } catch (err) {
      console.error('Error fetching footer settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch footer settings');
      setFooterSettings(defaultFooterSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFooterSettings();
  }, [fetchFooterSettings]);

  return {
    footerSettings,
    loading,
    error,
    refetch: fetchFooterSettings
  };
};